/* eslint @typescript-eslint/no-explicit-any: 0 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import { Tooltip } from 'primereact/tooltip';
import { useFormContext, useWatch } from 'react-hook-form';
import Select from 'react-select';

import { useTranslation } from '@/config/i18n';
import { apiRequest } from '@/services';
import { LESSON_CREATED_TYPE } from '@/shared/constants/graduation';
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/shared/constants/pagination';
import { GraduationTermLessonType } from '@/types/graduation';
import { LessonItemType } from '@/types/lesson';

// Show 5 rows at a time, the rest is reachable by scrolling.
const MAX_VISIBLE_LESSON_ROWS = 5;
const LESSON_ROW_HEIGHT = 58;
const LESSON_GRID_COLUMNS = '1fr 160px 120px';

type UnitOption = {
  label: string;
  value: number | null;
};

export type GraduationLessonsSource = {
  lessons: LessonItemType[];
  total: number;
  loading: boolean;
  /**
   * True once the list is known to be complete - including the legitimate "no admin
   * lesson exists" case. Saving before that would submit an empty grid and wipe the
   * stored settings, so it must be told apart from `lessons.length === 0`.
   */
  loaded: boolean;
  /** True when the list could not be loaded in full - saving must be blocked. */
  failed: boolean;
};

/**
 * Loads every admin-created ART SHIFT content shown in each コーチ・課金設定 item.
 *
 * The whole list is needed at once: saving a term submits the FULL grid (one row per
 * admin lesson, including the ones left OFF), so a partially loaded list would silently
 * revoke the lessons that never rendered.
 */
export const useGraduationLessons = (): GraduationLessonsSource => {
  const { apiService } = apiRequest();

  const [lessons, setLessons] = useState<LessonItemType[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const loadingRef = useRef(false);

  useEffect(() => {
    const fetchAllAdminLessons = async () => {
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      setFailed(false);

      // First page with the default limit: it already carries pagination.total_item and
      // is enough to paint the list, so nothing is wasted when the total fits in it.
      const { data: firstPage } = await apiService.lessons.index({
        created_type: LESSON_CREATED_TYPE.ADMIN,
        limit: DEFAULT_PER_PAGE,
        page: DEFAULT_PAGE,
      });

      if (!firstPage) {
        setFailed(true);
        setLoading(false);
        loadingRef.current = false;
        return;
      }

      const firstRows: LessonItemType[] = firstPage.data ?? [];
      const totalItem = firstPage.pagination?.total_item ?? firstRows.length;

      setLessons(firstRows);
      setTotal(totalItem);

      // Whole list already delivered -> no second request.
      if (firstRows.length >= totalItem) {
        setLoaded(true);
        setLoading(false);
        loadingRef.current = false;
        return;
      }

      // Otherwise ask for exactly as many as the server reported. No hard-coded page
      // size to outgrow, and the first page stays on screen meanwhile.
      const { data: fullPage } = await apiService.lessons.index({
        created_type: LESSON_CREATED_TYPE.ADMIN,
        limit: totalItem,
        page: DEFAULT_PAGE,
      });
      const allRows: LessonItemType[] = fullPage?.data ?? [];

      // Fail loud rather than letting the admin configure a subset of the contents.
      // Keep the first page rendered: replacing it with a partial list would hide rows.
      const isComplete = allRows.length === totalItem;
      if (isComplete) setLessons(allRows);
      setFailed(!isComplete);
      setLoaded(isComplete);
      setLoading(false);
      loadingRef.current = false;
    };

    fetchAllAdminLessons();
  }, []);

  return { lessons, total, loading, loaded, failed };
};

type Props = {
  termIndex: number;
} & GraduationLessonsSource;

const GraduationTermLessons: React.FC<Props> = ({
  termIndex,
  lessons,
  total,
  loading,
  loaded,
  failed,
}) => {
  const [t] = useTranslation('');
  const { control, getValues, setValue } = useFormContext();

  const fieldName = `terms.${termIndex}.lessons`;

  const termLessons: GraduationTermLessonType[] =
    useWatch({ control, name: fieldName }) ?? [];

  const settingByLessonId = useMemo(() => {
    const map = new Map<number, GraduationTermLessonType>();
    termLessons.forEach((item) => map.set(item.lesson_id, item));
    return map;
  }, [termLessons]);

  const updateLessonSetting = (
    lessonId: number,
    patch: Partial<GraduationTermLessonType>,
  ) => {
    const current: GraduationTermLessonType[] =
      (getValues(fieldName) as GraduationTermLessonType[]) ?? [];
    const index = current.findIndex((item) => item.lesson_id === lessonId);

    const next = [...current];
    if (index >= 0) {
      next[index] = { ...next[index], ...patch };
    } else {
      next.push({
        lesson_id: lessonId,
        is_available: false,
        unit_limit: null,
        is_shared: false,
        ...patch,
      });
    }

    setValue(fieldName, next, { shouldDirty: true, shouldValidate: false });
  };

  const handleToggleAvailable = (lessonId: number, checked: boolean) => {
    // Turning the coach permission off also drops the unit limit and the
    // permission shared with the coach's students.
    updateLessonSetting(
      lessonId,
      checked
        ? { is_available: true }
        : { is_available: false, unit_limit: null, is_shared: false },
    );
  };

  /**
   * Choices for the per-lesson unit cap.
   *
   * Built from the units a learner can actually open (`total_unit_public`), not from
   * `lessons.total_unit` - that column counts every unit ever created, drafts included, and is
   * never lowered, so it promises a ceiling nobody can reach. Same source and same shape as the
   * add-student dropdown, so one lesson does not offer two different sets of numbers.
   *
   * Stops at n-1 because 全て already covers "all of them" - and covers it as a live value: it is
   * stored as NULL and re-read on every request, so publishing another unit raises the cap on its
   * own, while a number stays where it was put.
   *
   * 0 sits right after 全て, the same place the add-student dropdown puts it. It means the lesson
   * IS granted to the coach with every unit locked ("has to be bought first") - not the same as
   * unticking 受講可能, which grants the lesson at all. It must reach the payload as 0: an empty
   * value is 全て, the highest cap there is, and the sync only ever raises a cap.
   */
  const buildUnitOptions = (lesson: LessonItemType): UnitOption[] => [
    { label: t('graduation.unit_all'), value: null },
    { label: '0', value: 0 },
    ...Array.from({ length: Math.max((lesson.total_unit_public ?? 0) - 1, 0) }, (_, i) => ({
      label: `${i + 1}`,
      value: i + 1,
    })),
  ];

  // Negative margins let the divider span the whole term card width.
  return (
    <div className="-mx-4 mt-2 px-4 pt-4 border-t border-gray-200">
      <div className="flex items-center gap-[10px] mb-3">
        <span className="text-[14px] font-bold text-gray-900">
          {t('graduation.art_shift_content')}
        </span>
        <span className="text-[12px] text-gray-500 bg-gray-100 rounded-full px-[10px] py-[2px]">
          {t('graduation.content_count', { value: total })}
        </span>
      </div>

      <div className="border border-gray-200 rounded-[10px] overflow-hidden">
        <div
          className="grid items-center bg-gray-50 px-5 py-[10px] border-b border-gray-200 text-[12px] font-semibold text-gray-500"
          style={{ gridTemplateColumns: LESSON_GRID_COLUMNS }}
        >
          <span>{t('graduation.lesson_name_column')}</span>
          <span>{t('graduation.unit_limit_column')}</span>
          <span>{t('graduation.lesson_usage_right')}</span>
        </div>

        <div
          className="overflow-y-auto"
          style={{ maxHeight: MAX_VISIBLE_LESSON_ROWS * LESSON_ROW_HEIGHT }}
        >
          {lessons.map((lesson) => {
            const setting = settingByLessonId.get(lesson.id);
            const isAvailable = !!setting?.is_available;
            const isRowDisabled = !isAvailable;
            const unitOptions = buildUnitOptions(lesson);
            // A stored cap can fall outside the current choices - unpublishing a unit shrinks the
            // list without touching what was saved. Show the stored number as it is instead of
            // snapping to the first option: that option is 全て, the highest ceiling there is, so
            // falling back to it would quietly widen the grant on the next save (and raiseOnly
            // never takes it back). Mirrors how the add-student table keeps its stored value.
            const storedUnitLimit = setting?.unit_limit ?? null;
            const unitValue =
              unitOptions.find((option) => option.value === storedUnitLimit) ??
              (storedUnitLimit === null
                ? unitOptions[0]
                : { label: `${storedUnitLimit}`, value: storedUnitLimit });

            const inputId = `term-${termIndex}-lesson-${lesson.id}`;
            const tooltipClass = `tooltip-point-${inputId}`;

            return (
              <div
                key={lesson.id}
                className="grid items-center px-5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                style={{
                  gridTemplateColumns: LESSON_GRID_COLUMNS,
                  height: LESSON_ROW_HEIGHT,
                }}
              >
                <div className="min-w-0 flex items-center gap-[10px] pr-4">
                  <Checkbox
                    inputId={inputId}
                    className="border border-gray-300 rounded-[4px] overflow-hidden shrink-0"
                    checked={isAvailable}
                    onChange={(e) =>
                      handleToggleAvailable(lesson.id, !!e.checked)
                    }
                  />
                  {/* Japanese names have no word breaks: keep one line + ellipsis
                      and expose the full name through a tooltip. */}
                  <Tooltip target={`.${tooltipClass}`}>{lesson.name}</Tooltip>
                  <label
                    htmlFor={inputId}
                    data-pr-position="top"
                    className={`min-w-0 truncate text-[14px] text-gray-900 cursor-pointer ${tooltipClass}`}
                  >
                    {lesson.name}
                  </label>
                </div>

                <Select
                  className="infinity-select w-[100px]"
                  classNamePrefix="select"
                  options={unitOptions}
                  value={unitValue}
                  isSearchable={false}
                  isDisabled={isRowDisabled}
                  placeholder={t('graduation.unit_placeholder')}
                  noOptionsMessage={() => t('graduation.lesson_no_data')}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 1100 }),
                    control: (base) => ({
                      ...base,
                      minHeight: 34,
                      backgroundColor: isRowDisabled ? '#F9FAFB' : '#FFFFFF',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      fontSize: 13,
                      color: isRowDisabled ? '#98A2B3' : '#101828',
                    }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    dropdownIndicator: (base) => ({ ...base, padding: 0 }),
                  }}
                  onChange={(option) =>
                    updateLessonSetting(lesson.id, {
                      unit_limit: (option as UnitOption)?.value ?? null,
                    })
                  }
                />

                <InputSwitch
                  checked={!!setting?.is_shared}
                  disabled={isRowDisabled}
                  onChange={(e) =>
                    updateLessonSetting(lesson.id, { is_shared: !!e.value })
                  }
                />
              </div>
            );
          })}

          {failed && !loading && (
            <div className="text-[14px] text-red-500 text-center py-6">
              {t('graduation.lesson_load_failed')}
            </div>
          )}

          {loaded && !lessons.length && !loading && (
            <div className="text-[14px] text-gray-500 text-center py-6">
              {t('graduation.lesson_no_data')}
            </div>
          )}

          {loading && (
            <div className="flex justify-center py-3 text-gray-400">
              <i className="pi pi-spin pi-spinner text-[18px]" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GraduationTermLessons;
